import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { TokenContextProvider } from './TokenProvider/token.context';
import { EVMProviders } from './EVMProvider';
import { QueryClientProvider } from '@tanstack/react-query';
import { bridgeQueryClient } from '@/utils/bridge/queryClient';

// Lazy-load heavy provider modules — deferred from initial bundle.
// EVMProviders is imported eagerly so createAppKit runs before any
// child component can call useAppKit.
const LazySodaxProviders = lazy(() =>
  import('./SodaxProvider').then((mod) => ({ default: mod.SodaxProviders }))
);

const LazyAleoProviders = lazy(() =>
  import('./WalletProvider').then((mod) => ({ default: mod.WalletProviders }))
);

// TransactionProvider uses useAleoWallet (from @provablehq) — defer it
// so the Aleo SDK stays out of the initial bundle.
const LazyTransactionProvider = lazy(() =>
  import('./TransactionProvider').then((mod) => ({ default: mod.TransactionProvider }))
);

const DeferredProvider = ({
  children,
  Lazy,
}: {
  children: ReactNode;
  Lazy: React.LazyExoticComponent<React.ComponentType<{ children: ReactNode }>>;
}) => {
  const [ready, setReady] = useState(false);
  useEffect(() => setReady(true), []);

  if (!ready) return null;

  return (
    <Suspense fallback={null}>
      <Lazy>{children}</Lazy>
    </Suspense>
  );
};

const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <QueryClientProvider client={bridgeQueryClient}>
      <DeferredProvider Lazy={LazyAleoProviders}>
        <DeferredProvider Lazy={LazyTransactionProvider}>
          <DeferredProvider Lazy={LazySodaxProviders}>
            <EVMProviders>
              <TokenContextProvider>
                {children}
              </TokenContextProvider>
            </EVMProviders>
          </DeferredProvider>
        </DeferredProvider>
      </DeferredProvider>
    </QueryClientProvider>
  );
};

export default AppProvider;
